<!-- Kit — the settings screen, and a tab.
     Units, the bar and the collars first, then how many pairs of each plate
     the gym actually has and what colour they are, then each lift's default
     warm-ups.

     A TAB ROOT, so it takes the bar plus a large title and no back button.
     See src/pages/SHELL.md.

     THE COLOUR IS THE POINT. Plate colour is seed data on the plate record and
     every drawing in the app reads it — the sleeve on Ramp, the bar on Bar, the
     sample at the top of this screen. A lifter whose gym has black 25s
     recolours them here and the whole app follows. That is why every plate is
     a tile carrying its own colour, one tap from its editor.

     PAIRS ARE STATED HONESTLY. `pairs: 0` means the gym does not have that
     plate, and lib/loading.js already excludes it from every stack it builds.
     So a zero tile stays visible and says "none", rather than vanishing — a
     plate you cannot see is a plate you cannot put back. -->
<template>
  <IonPage class="work-up kit-page">
    <AppHeader title="Kit" />

    <IonContent :fullscreen="true">
      <AppLargeTitle title="Kit" />

      <div v-if="!kit" class="empty">
        <p class="empty-line serif">No kit yet.</p>
        <p class="note">Nothing to load against.</p>
        <button type="button" class="wu-btn" :disabled="!ready" @click="makeKit">Add kit</button>
      </div>

      <div v-else class="wrap">
        <!-- What the kit actually looks like: one of each plate owned,
             heaviest inside, so a colour change is visible where it will be
             read. -->
        <div class="sample">
          <BarSleeve
            compact
            :stack="sample"
            :plates="kitPlates"
            :unit="kit.unit"
            :collar-weight="Number(kit.collarWeight) || 0"
          />
          <p v-if="!sample.length" class="note">No plates. The bar alone.</p>
        </div>

        <!-- BAR ------------------------------------------------------------ -->
        <section class="group" aria-labelledby="kit-bar">
          <h2 id="kit-bar" class="wu-label group-label">Bar</h2>

          <div class="wu-row">
            <span id="unit-label" class="key">Units</span>
            <div class="wu-seg" role="group" aria-labelledby="unit-label">
              <button
                v-for="u in UNITS"
                :key="u"
                type="button"
                :aria-pressed="kit.unit === u"
                @click="setUnit(u)"
              >{{ u }}</button>
            </div>
          </div>

          <!-- Switching the unit does NOT touch the plates. An lb kit wants lb
               sizes, not metric ones converted — but a lifter who has already
               customised a kit must be asked before it is thrown away, so this
               offers and waits. Taking it is still reversible: the old rows are
               held out of the list and only deleted when the undo window
               shuts. -->
          <div v-if="offer" class="offer" role="status">
            <p class="offer-line">Unit is {{ offer }}. These plates are still {{ offer === 'lb' ? 'kg' : 'lb' }} sizes.</p>
            <div class="offer-actions">
              <button type="button" class="wu-btn wu-btn--row" :disabled="!ready" @click="loadSeed">Load {{ offer }} plates</button>
              <button type="button" class="wu-btn-quiet wu-btn--row" @click="offer = ''">Keep these</button>
            </div>
          </div>

          <label class="wu-row">
            <span class="key">Bar</span>
            <span class="val">
              <input
                class="wu-field-sm num-in"
                type="number"
                inputmode="decimal"
                min="0"
                step="0.5"
                :value="kit.barWeight"
                @input="setNumber(kit, 'barWeight', $event, 0, 1000, saveKit)"
                @blur="resync($event, kit.barWeight)"
              >
              <span class="val-unit">{{ kit.unit }}</span>
            </span>
          </label>

          <label class="wu-row">
            <span class="key">Collars</span>
            <span class="val">
              <input
                class="wu-field-sm num-in"
                type="number"
                inputmode="decimal"
                min="0"
                step="0.25"
                :value="kit.collarWeight"
                @input="setNumber(kit, 'collarWeight', $event, 0, 100, saveKit)"
                @blur="resync($event, kit.collarWeight)"
              >
              <span class="val-unit">{{ kit.unit }} each</span>
            </span>
          </label>

          <p class="note bar-note">
            Empty bar {{ fmt(emptyBar) }} {{ kit.unit }}; collars go on with the first plate.<template v-if="!Number(kit.collarWeight)"> 0 is spring clips.</template>
          </p>
        </section>

        <!-- PLATES --------------------------------------------------------- -->
        <section class="group" aria-labelledby="kit-plates">
          <h2 id="kit-plates" class="wu-label group-label">Plates · tap to set pairs and colour</h2>

          <div class="tiles">
            <template v-for="(p, i) in kitPlates" :key="p.id">
              <button
                type="button"
                class="tile"
                :class="{ none: !pairsOf(p), open: editing === p.id }"
                :aria-expanded="editing === p.id"
                :aria-controls="editing === p.id ? 'plate-editor' : undefined"
                :aria-label="`${fmt(Number(p.weight) || 0)} ${kit.unit}, ${pairsText(p)}. Edit`"
                @click="toggleEditor(p)"
              >
                <span
                  class="dot"
                  :class="{ pale: plateTone(p.colour).pale, rim: plateTone(p.colour).dark }"
                  :style="{ background: p.colour || 'var(--faint)' }"
                  aria-hidden="true"
                />
                <span class="tile-text">
                  <span class="tile-weight">{{ fmt(Number(p.weight) || 0) }}</span>
                  <span class="tile-pairs">{{ pairsText(p) }}</span>
                </span>
              </button>

              <!-- THE EDITOR, under the row of the plate it edits: everything a
                   plate row used to hold — weight, pairs, colour, Remove. -->
              <div
                v-if="editorAfter === i"
                id="plate-editor"
                class="editor"
                role="group"
                :aria-label="`The ${fmt(Number(openPlate.weight) || 0)} ${kit.unit} plate`"
              >
                <label class="ed-row">
                  <span class="key">Weight</span>
                  <span class="val">
                    <input
                      :id="`w${openPlate.id}`"
                      class="wu-field-sm num-in"
                      type="number"
                      inputmode="decimal"
                      min="0"
                      step="0.25"
                      :aria-label="`Plate weight in ${kit.unit}`"
                      :value="openPlate.weight"
                      @input="openPlate && setNumber(openPlate, 'weight', $event, 0, 1000, savePlate)"
                      @blur="onWeightBlur"
                    >
                    <span class="val-unit">{{ kit.unit }}</span>
                  </span>
                </label>

                <div class="ed-row">
                  <span class="key">
                    Pairs
                    <span class="key-note">{{ pairsOf(openPlate) ? 'in the gym' : 'none, the maths skips it' }}</span>
                  </span>
                  <span class="stepper">
                    <button type="button" class="step" aria-label="One pair fewer" :disabled="!pairsOf(openPlate)" @click="bump(openPlate, -1)">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12" /></svg>
                    </button>
                    <span class="count" aria-live="off">{{ pairsOf(openPlate) }}</span>
                    <button type="button" class="step" aria-label="One pair more" @click="bump(openPlate, 1)">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 6v12M6 12h12" /></svg>
                    </button>
                  </span>
                </div>

                <!-- The presets are the IPF set every gym already owns, and the
                     last control is the OS colour picker for everything else. -->
                <div class="ed-colours" role="group" aria-label="Colour">
                  <button
                    v-for="[label, hex] in PRESETS"
                    :key="hex"
                    type="button"
                    class="swatch"
                    :class="{ on: sameColour(openPlate.colour, hex) }"
                    :aria-label="label"
                    :aria-pressed="sameColour(openPlate.colour, hex)"
                    @click="setColour(openPlate, hex)"
                  >
                    <span
                      class="swatch-dot"
                      :class="{ pale: plateTone(hex).pale, rim: plateTone(hex).dark }"
                      :style="{ background: hex }"
                    />
                  </button>
                  <label class="custom">
                    <span class="sr-only">Any other colour</span>
                    <input
                      type="color"
                      :value="openPlate.colour || '#B8BDC1'"
                      @input="setColour(openPlate, $event.target.value)"
                    >
                    <span aria-hidden="true">Other</span>
                  </label>
                </div>

                <div class="ed-foot">
                  <button type="button" class="wu-link" @click="dropPlate(openPlate)">Remove</button>
                  <button type="button" class="wu-btn-quiet wu-btn--row" @click="closeEditor">Done</button>
                </div>
              </div>
            </template>

            <button type="button" class="tile add" :disabled="!ready" @click="newPlate">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 6v12M6 12h12" /></svg>
              Add plate
            </button>
          </div>
        </section>

        <!-- EACH LIFT'S DEFAULT WARM-UPS, and its usual last jump: the gap
             from the final warm-up to the top set. Both live on the lift,
             because they are how this lifter warms up. A row opens the
             lift's DEFAULT scheme, where the jump is edited too. Different
             warm-ups for one session are set from Lifts and never come back
             here. -->
        <section v-if="kitLifts.length" class="group" aria-labelledby="kit-warm">
          <h2 id="kit-warm" class="wu-label group-label">Warm-ups</h2>
          <button
            v-for="l in kitLifts"
            :key="l.id"
            type="button"
            class="wu-row lift-row"
            :aria-label="`${l.name}: ${liftSummary(l)}. Edit the default warm-ups`"
            @click="openScheme(l)"
          >
            <span class="key">{{ l.name }}</span>
            <span class="lift-sum">
              {{ liftSummary(l) }}
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </span>
          </button>
        </section>

        <!-- THIS PHONE -------------------------------------------------------
             Not part of the kit: how the app behaves on the phone in your
             hand, kept on this device (lib/prefs.js). Plates sliding on and
             off the bar is On unless turned off here, and always off under the
             phone's Reduce Motion, which this row says rather than hides. -->
        <section class="group phone" aria-labelledby="kit-phone">
          <h2 id="kit-phone" class="wu-label group-label">This phone</h2>
          <div class="wu-row">
            <span id="motion-label" class="key">
              Animate loading
              <span v-if="reducedMotion" class="key-note">off while Reduce Motion is on</span>
            </span>
            <div class="wu-seg" role="group" aria-labelledby="motion-label">
              <button
                v-for="o in ON_OFF"
                :key="o.label"
                type="button"
                :aria-pressed="animateLoading === o.on"
                @click="setMotion(o.on)"
              >{{ o.label }}</button>
            </div>
          </div>
          <div class="wu-row">
            <span class="key">The intro</span>
            <button type="button" class="wu-link" @click="showFirstRun">Show it again</button>
          </div>
        </section>

        <!-- YOUR DATA --------------------------------------------------------
             Everything lives on this phone and nowhere else — no account, no
             server. So the lifter can take it with them as one file and put it
             back here or on a new phone (lib/backup.js). Restoring REPLACES
             everything, which makes it the one action in the app that asks
             first; the question names what the file holds. -->
        <section class="group data" aria-labelledby="kit-data">
          <h2 id="kit-data" class="wu-label group-label">Your data</h2>
          <p class="note data-note">
            Everything is kept on this phone. A backup is one file — keep it in Files, iCloud Drive or an email — and restore it here or on a new phone. Clips stay in Photos.
          </p>
          <div class="data-actions">
            <button type="button" class="wu-btn-quiet wu-btn--row" :disabled="!ready || busy" @click="backUp">Back up</button>
            <label class="wu-btn-quiet wu-btn--row file-btn" :class="{ off: !ready || busy }">
              Restore
              <input type="file" accept="application/json,.json" :disabled="!ready || busy" aria-label="Restore from a backup file" @change="pickBackup">
            </label>
          </div>

          <div v-if="pending" class="offer restore" role="alertdialog" aria-labelledby="restore-line" aria-describedby="restore-note">
            <p id="restore-line" class="offer-line">Restore the backup from {{ pendingDate }}?</p>
            <p id="restore-note" class="note restore-note">{{ countsLine(pending.counts) }}. Everything on this phone now is replaced.</p>
            <div class="offer-actions">
              <button type="button" class="wu-btn wu-btn--row" :disabled="busy" @click="confirmRestore">Replace everything</button>
              <button type="button" class="wu-btn-quiet wu-btn--row" :disabled="busy" @click="pending = null">Cancel</button>
            </div>
          </div>
        </section>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { IonPage, IonContent, onIonViewWillLeave } from '@ionic/vue';
import { AppHeader, AppLargeTitle, removeWithUndo } from '../core/ui';
import BarSleeve from '../components/BarSleeve.vue';
import { plateTone } from '../components/PlatePad.vue';
import { debounce, haptic } from '../core/util.js';
import {
  kitsReady, addKit, saveKit,
  plates, platesReady, addPlate, savePlate, removePlate,
  lifts,
} from '../store.js';
import { currentKit, platesFor, seedIfEmpty, adoptNewDefaults, savedScheme, LB_PLATES } from '../lib/seed.js';
import { fmt, savedJump } from '../lib/loading.js';
import { animateLoading, reducedMotion, setAnimateLoading } from '../lib/prefs.js';
import { showFirstRun } from '../lib/firstRun.js';
import { collectBackup, readBackup, restoreBackup, backupFilename, REFUSED } from '../lib/backup.js';
import { shareOrDownload } from '../core/export.js';
import { toast } from '../core/util.js';
import { flushUndo } from '../core/ui';
import { useRouter } from 'vue-router';

const router = useRouter();

const UNITS = ['kg', 'lb'];

// ---- your data: back up and restore ----
const busy = ref(false);
/** A backup read from a file and waiting for "Replace everything". */
const pending = ref(null);
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const countsLine = (c) => [plural(c.lifts, 'lift'), plural(c.sets, 'set'), plural(c.plates, 'plate')].join(' · ');
const pendingDate = computed(() => {
  const d = new Date(pending.value?.exportedAt ?? '');
  return Number.isNaN(d.getTime())
    ? 'an earlier day'
    : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
});

async function backUp() {
  if (busy.value) return;
  busy.value = true;
  try {
    flushNow();
    const backup = await collectBackup();
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const r = await shareOrDownload(blob, backupFilename(), 'Work Up backup');
    if (r !== 'aborted') toast(`Backed up · ${countsLine(backup.counts)}.`);
  } catch {
    toast("Couldn't make a backup.");
  } finally {
    busy.value = false;
  }
}

async function pickBackup(event) {
  const file = event.target.files?.[0];
  event.target.value = ''; // so the same file can be picked again
  if (!file) return;
  const r = readBackup(await file.text());
  if (!r.ok) { toast(REFUSED[r.reason]); return; }
  pending.value = r;
}

async function confirmRestore() {
  if (!pending.value || busy.value) return;
  busy.value = true;
  try {
    // A removal still in its undo window deletes by id when the window shuts.
    // Commit it now, or it would land on a RESTORED record with the same id.
    flushUndo();
    flushNow();
    await restoreBackup(pending.value.backup);
    haptic('pop');
    // The store rehydrates from what was written, and its id sequence starts
    // past the highest restored id.
    location.reload();
  } catch {
    busy.value = false;
    toast("Couldn't restore that backup.");
  }
}
const ON_OFF = [{ label: 'On', on: true }, { label: 'Off', on: false }];

function setMotion(on) {
  if (animateLoading.value === on) return;
  setAnimateLoading(on);
  haptic('selection');
}

// IPF competition colours, the ones a plate is most likely to already be.
// Anything else comes from the OS colour picker at the end of the row.
const PRESETS = [
  ['Red', '#D0202E'],
  ['Blue', '#1F4E9C'],
  ['Yellow', '#F2C12E'],
  ['Green', '#2F8A4C'],
  ['White', '#F4F4F1'],
  ['Black', '#1B1D1F'],
  ['Silver', '#B8BDC1'],
];

// The kg seed, for switching an lb kit back. `lib/seed.js` owns the canonical
// list and exports only LB_PLATES, so this is a copy of KG_PLATES and has to be
// kept beside it — see the note in the handover; seed.js should export it.
const KG_SET = [
  { weight: 25, pairs: 6, colour: '#D0202E' },
  { weight: 20, pairs: 2, colour: '#1F4E9C' },
  { weight: 15, pairs: 2, colour: '#F2C12E' },
  { weight: 10, pairs: 2, colour: '#2F8A4C' },
  { weight: 5, pairs: 2, colour: '#F4F4F1' },
  { weight: 2.5, pairs: 2, colour: '#1B1D1F' },
  { weight: 1.25, pairs: 2, colour: '#B8BDC1' },
  { weight: 0.5, pairs: 2, colour: '#B8BDC1' },
  { weight: 0.25, pairs: 2, colour: '#B8BDC1' },
];

// NOTHING MAY ALLOCATE AN ID BEFORE THE STORE HAS HYDRATED. Ids come from one
// shared sequence, so a row created while the read is in flight takes an id a
// stored row already has. Every control that creates a row is disabled until
// this flips.
const ready = ref(false);
Promise.all([kitsReady, platesReady])
  .then(() => seedIfEmpty())
  .then(() => adoptNewDefaults())
  .catch(() => {})
  .then(() => { ready.value = true; });

const kit = computed(() => currentKit());

// THE TILES HOLD STILL WHILE ONE IS OPEN. They sort by weight, and the weight
// is being typed in the open editor: without this, typing "2" into the 25
// would fling the tile and its editor to the end of the grid mid-number. The
// order is frozen when an editor opens and re-sorted when it closes.
const frozen = ref(null);
const sortWeight = (p) => (frozen.value?.has(String(p.id))
  ? frozen.value.get(String(p.id))
  : Number(p.weight));

const kitPlates = computed(() => plates.value
  .filter((p) => !kit.value || !p.kit || String(p.kit) === String(kit.value.id))
  .slice()
  .sort((a, b) => sortWeight(b) - sortWeight(a)));

// One of each pair actually owned. `platesFor` already drops the zero rows.
const sample = computed(() => (kit.value ? platesFor(kit.value).map((p) => Number(p.weight)) : []));

// The bar alone: an empty bar has no collars on it.
const emptyBar = computed(() => (kit.value ? Number(kit.value.barWeight) || 0 : 0));

// A write per keystroke is a write per keystroke. Coalesced by row, so editing
// two fields quickly saves both rather than only the last one to be touched.
const dirty = new Map();
const flushSaves = debounce(() => {
  for (const save of dirty.values()) save();
  dirty.clear();
}, 250);
function queueSave(key, save) {
  dirty.set(key, save);
  flushSaves();
}
function flushNow() {
  for (const save of dirty.values()) save();
  dirty.clear();
}
onBeforeUnmount(flushNow);
// A tab is kept alive when you leave it, so unmount alone is not "leaving".
onIonViewWillLeave(flushNow);

/** Mid-typing gives '' and '-' — leave the model alone rather than storing NaN. */
function setNumber(row, field, event, min, max, save) {
  const v = parseFloat(event.target.value);
  if (!Number.isFinite(v)) return;
  row[field] = Math.min(max, Math.max(min, v));
  queueSave(`${row.id}:${field}`, () => save(row));
}

/** Put the model's value back in the field once the person has left it. */
function resync(event, value) {
  event.target.value = value;
  flushNow();
}

function makeKit() {
  if (!ready.value) return;
  addKit({ name: 'My bar', unit: 'kg', barWeight: 20, collarWeight: 2.5 });
  haptic('pop');
}

const offer = ref('');

function setUnit(u) {
  if (!kit.value || kit.value.unit === u) return;
  kit.value.unit = u;
  saveKit(kit.value);
  haptic('selection');
  // Only offer when there is something to replace.
  offer.value = kitPlates.value.length ? u : '';
}

/**
 * Replace the plates with the seed for the unit now selected.
 *
 * The old rows are taken out of the list but NOT deleted; the delete happens
 * when the undo window closes. So the destructive half never runs while it can
 * still be taken back, which is what makes this an offer rather than a dialog.
 */
function loadSeed() {
  const u = offer.value;
  offer.value = '';
  if (!ready.value || !kit.value) return;
  closeEditor();
  const old = kitPlates.value.slice();
  const ids = new Set(old.map((p) => String(p.id)));
  plates.value = plates.value.filter((p) => !ids.has(String(p.id)));
  const seed = u === 'lb' ? LB_PLATES : KG_SET;
  const added = seed.map((s) => addPlate({ ...s, kit: kit.value.id }));
  haptic('pop');
  removeWithUndo({
    text: `${seed.length} ${u} plates loaded, ${old.length} replaced.`,
    commit: () => { for (const p of old) removePlate(p.id); },
    revert: () => {
      for (const p of added) removePlate(p.id);
      plates.value = [...old, ...plates.value];
    },
  });
}

// --- the plate editor --------------------------------------------------------
const editing = ref(null);
const openPlate = computed(() => kitPlates.value.find((p) => p.id === editing.value) ?? null);

/**
 * Which tile the editor follows: the last tile in the open plate's row of
 * three, so it opens under the row rather than splitting it. The Add tile is
 * the grid's last cell; when the open plate shares its row, the editor goes
 * after the last PLATE and the Add tile wraps below it.
 */
const editorAfter = computed(() => {
  if (!openPlate.value) return -1;
  const i = kitPlates.value.indexOf(openPlate.value);
  const rowEnd = Math.floor(i / 3) * 3 + 2;
  return Math.min(rowEnd, kitPlates.value.length - 1);
});

// A focused input that is removed from the page still fires blur, and closing
// the editor removes it — by then there is no open plate to read back.
function onWeightBlur(event) {
  if (openPlate.value) resync(event, openPlate.value.weight);
  else flushNow();
}

function openEditor(p) {
  frozen.value = new Map(kitPlates.value.map((r) => [String(r.id), Number(r.weight)]));
  editing.value = p.id;
}

function closeEditor() {
  flushNow();
  editing.value = null;
  frozen.value = null;
}

function toggleEditor(p) {
  if (editing.value === p.id) closeEditor();
  else openEditor(p);
  haptic('tick');
}

const pairsOf = (p) => Number(p?.pairs) || 0;
function pairsText(p) {
  const n = pairsOf(p);
  if (!n) return 'none';
  return n === 1 ? '1 pair' : `${n} pairs`;
}

const sameColour = (a, b) => String(a || '').toLowerCase() === String(b).toLowerCase();

function setColour(p, hex) {
  p.colour = hex;
  queueSave(`${p.id}:colour`, () => savePlate(p));
  haptic('tick');
}

function bump(p, by) {
  const next = Math.max(0, pairsOf(p) + by);
  if (next === Number(p.pairs)) return;
  p.pairs = next;
  queueSave(`${p.id}:pairs`, () => savePlate(p));
  haptic('selection');
}

function newPlate() {
  if (!ready.value || !kit.value) return;
  // Half the smallest plate owned is the one a lifter is most likely to be
  // missing; a fresh kit gets the bar's own size.
  const smallest = kitPlates.value.length
    ? Math.min(...kitPlates.value.map((p) => Number(p.weight) || 0))
    : 0;
  const weight = smallest > 0
    ? Math.max(0.25, Math.round((smallest / 2) * 100) / 100)
    : (kit.value.unit === 'lb' ? 45 : 20);
  const row = addPlate({ weight, pairs: 1, colour: '#B8BDC1', kit: kit.value.id });
  haptic('pop');
  // Straight into its editor, weight first: a new plate is a plate to describe.
  flushNow();
  openEditor(row);
  nextTick(() => {
    const el = document.getElementById(`w${row.id}`);
    el?.scrollIntoView({ block: 'center' });
    el?.focus();
  });
}

/**
 * Remove, reversibly. The tile leaves the grid at once and the record is only
 * deleted when the undo window shuts — so the id survives an undo, which
 * re-adding it would not.
 */
function dropPlate(p) {
  const row = { ...p };
  const at = plates.value.findIndex((r) => String(r.id) === String(p.id));
  if (editing.value === p.id) closeEditor();
  plates.value = plates.value.filter((r) => String(r.id) !== String(p.id));
  haptic('pop');
  removeWithUndo({
    text: `${fmt(Number(row.weight) || 0)} ${kit.value?.unit ?? ''} removed.`,
    commit: () => removePlate(row.id),
    revert: () => {
      const next = plates.value.slice();
      next.splice(Math.max(0, at), 0, row);
      plates.value = next;
    },
  });
}

// --- warm-ups ----------------------------------------------------------------
// Same order as Lifts, so a lift is where the lifter last saw it.
const ORDER = ['squat', 'bench', 'deadlift', 'other'];
const kitLifts = computed(() => lifts.value.slice().sort(
  (a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind),
));

// Steps plus the last warm-up, as the scheme counts them; the bar is not one.
function warmUps(l) {
  const n = savedScheme(l).steps.length;
  return n ? `${n + 1} warm-ups` : 'No warm-ups';
}

function liftSummary(l) {
  const u = kit.value?.unit ?? 'kg';
  return `${warmUps(l)} · last jump ${fmt(savedJump(l, u))} ${u}`;
}

// The DEFAULT door into the scheme editor. Lifts opens the same screen for today.
const openScheme = (l) => router.push(`/tabs/lifts/${l.id}/scheme?for=default`);
</script>

<style scoped>
/* The sample bar runs in from the screen's edge, like every bar in the app. */
.kit-page { --bleed: var(--wu-pad); }

.wrap { padding: 0 var(--wu-pad) 24px; }

/* THE SAMPLE: the design's small bar, about half the phone wide. It is drawn
   to scale, so a bar carrying one of every plate is tall and narrow. */
.sample {
  padding-top: 14px;
  --sleeve-span: min(48vw, 220px);
}

.sample .note { margin-top: 0; }

.group { padding-top: 22px; }
.group:first-of-type { padding-top: 10px; }

.group-label { margin: 0 0 8px; }

.key {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font: 400 0.875rem/1.3 var(--face-mono);
  color: var(--ink);
}

.key-note {
  font-size: 0.6875rem;
  color: var(--dim);
}

.note {
  margin: 8px 0 0;
  font: 400 0.75rem/1.5 var(--face-mono);
  color: var(--dim);
}

.bar-note { margin: 0; padding: 4px 0 0; }

/* THE SWITCHES TAKE A 52PX TAP, and look the same. The pill is the design's
   38px; a transparent layer over each button reaches 7px above and below it,
   which the 56px row has room for. Units and Animate loading alike. */
.wu-row .wu-seg > button { position: relative; }
.wu-row .wu-seg > button::after {
  content: '';
  position: absolute;
  inset: -7px 0;
}

/* A value and its unit: the fields line up down the rows because every unit
   column is at least as wide as "kg each". */
.val { display: inline-flex; align-items: center; gap: 8px; }

input.num-in {
  width: 76px;
  padding: 0 12px;
  text-align: right;
}

.val-unit {
  min-width: 58px;
  font: 400 0.75rem/1 var(--face-mono);
  color: var(--dim);
}

/* --- the unit offer --------------------------------------------------------- */
.offer {
  padding: 4px 0 14px;
}

.offer-line {
  margin: 0 0 10px;
  font: 400 0.8125rem/1.5 var(--face-mono);
  color: var(--ink);
}

.offer-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.offer-actions .wu-btn { flex: 1 1 auto; }

/* --- plates ----------------------------------------------------------------- */
.tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.tile {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 52px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid var(--field-line);
  border-radius: 14px;
  background: var(--field);
  color: var(--ink);
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.tile:active { background: var(--seg); }

/* The open tile is edged in ink: shape, not colour, says which one. */
.tile.open { border-color: var(--ink); box-shadow: 0 0 0 1px var(--ink); }

.dot {
  flex: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.dot.pale,
.swatch-dot.pale { box-shadow: inset 0 0 0 1px var(--pale-edge); }
.dot.rim,
.swatch-dot.rim { box-shadow: inset 0 0 0 1px var(--dark-rim); }

.tile-text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }

.tile-weight {
  font: 500 0.875rem/1.2 var(--face-mono);
  font-variant-numeric: tabular-nums;
}

.tile-pairs {
  font: 400 0.625rem/1.2 var(--face-mono);
  color: var(--dim);
  white-space: nowrap;
}

/* None in the gym: still there, still tappable, plainly not in play. */
.tile.none .tile-weight { color: var(--dim); }
.tile.none .dot { opacity: 0.45; }

/* One line at a third of an SE's width: "Add plate" at 12px is 65px. */
.tile.add {
  justify-content: center;
  gap: 4px;
  padding: 0 8px;
  border-style: dashed;
  background: transparent;
  font: 400 0.75rem/1 var(--face-mono);
  white-space: nowrap;
}

.tile.add svg,
.step svg,
.lift-sum svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.75;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.tile.add:disabled { color: var(--faint); cursor: default; }

/* --- the plate editor --------------------------------------------------------- */
.editor {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  padding: 4px 16px 8px;
  border: 1px solid var(--field-line);
  border-radius: 18px;
  background: var(--field);
}

.ed-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 60px;
  border-bottom: 1px solid var(--line);
}

.stepper { display: flex; align-items: center; gap: 10px; }

.step {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: 1px solid var(--field-line);
  border-radius: 12px;
  background: var(--field);
  color: var(--ink);
  cursor: pointer;
}

.step:active { background: var(--seg); }
.step:disabled { border-style: dashed; border-color: var(--faint); color: var(--faint); cursor: default; }

.count {
  min-width: 2.5ch;
  text-align: center;
  font: 500 0.9375rem/1 var(--face-mono);
  font-variant-numeric: tabular-nums;
}

.ed-colours {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}

/* 44px targets around a 26px dot. The chosen one is ringed in ink. */
.swatch {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}

.swatch-dot { width: 26px; height: 26px; border-radius: 50%; }

.swatch.on .swatch-dot {
  box-shadow: 0 0 0 2px var(--field), 0 0 0 3.5px var(--ink);
}

.custom {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  margin-left: 4px;
  padding: 0 12px 0 8px;
  border: 1px solid var(--field-line);
  border-radius: 22px;
  font: 400 0.8125rem/1 var(--face-mono);
  color: var(--ink);
  cursor: pointer;
}

/* The well shows the plate's current colour, which may be the field's own
   black or white: a hairline keeps it visible in both schemes. */
.custom input[type='color'] {
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  box-shadow: 0 0 0 1px var(--faint);
  cursor: pointer;
}

.custom input[type='color']::-webkit-color-swatch-wrapper { padding: 0; }
.custom input[type='color']::-webkit-color-swatch { border: 0; border-radius: 50%; }

.ed-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
}

/* --- warm-ups ----------------------------------------------------------------- */
.lift-row {
  width: 100%;
  margin: 0;
  padding: 0;
  border-right: 0;
  border-bottom: 0;
  border-left: 0;
  background: transparent;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.lift-row:last-child,
.phone .wu-row { border-bottom: 1px solid var(--line); }

/* ---- your data ---- */
.data-note { margin: 0 0 12px; }
.data-actions { display: flex; gap: 10px; }
.data-actions > * { flex: 1 1 0; }
/* A label dressed as the quiet button, the file input laid over it: tapping
   anywhere on "Restore" opens the Files picker. */
.file-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
}
.file-btn input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.file-btn.off { opacity: 0.45; pointer-events: none; }
.restore { margin-top: 14px; }
.restore-note { margin: 6px 0 0; }
.restore .offer-actions { margin-top: 14px; }
.lift-row:active { background: var(--seg); }

.lift-sum {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font: 400 0.75rem/1.3 var(--face-mono);
  color: var(--dim);
  text-align: right;
}

.lift-sum svg { flex: none; color: var(--faint); }

/* --- no kit ----------------------------------------------------------------- */
.empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 24px var(--wu-pad);
}

.empty-line { margin: 0; font-size: 1.625rem; line-height: 1.2; }
.empty .note { margin: 0 0 8px; }
</style>
